import { FC, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { ShopifyRegion } from "../ShopifyGeoSpatialDashboard.data";
import DefinitionTooltip from "src/components/common/DefinitionTooltip";
import { useLanguage } from "src/hooks/UseLanguage";
import {
  type GeoCrossFilter,
  type HeatmapHover,
  applyCityCrossFilter,
  applyHeatmapCrossFilter,
  geoDigits,
  getFixedTooltipPosition,
  heatLevelClass,
  inferPakistanRegionSlug,
  isOthersRegion,
  resolveRegionShapeKey,
} from "./ShopifyGeoSpatialDashboard.utils";
import ShopifyGeoSparklineMetric from "./ShopifyGeoSparklineMetric";
import ShopifyGeoProductSalesBarChart from "./ShopifyGeoProductSalesBarChart";
import ShopifyGeoPakistanCitiesMap from "./ShopifyGeoPakistanCitiesMap";
import ShopifyGeoNotAvailableMapPlaceholder from "./ShopifyGeoNotAvailableMapPlaceholder";
import "../../../styles/ShopifyGeoRegionCard.scss";
import "../../../styles/ShopifyGeoHeatmapPanel.scss";

const ShopifyGeoRegionCard: FC<{
  region: ShopifyRegion;
  crossFilter: GeoCrossFilter;
  onCrossFilter: (_next: GeoCrossFilter) => void;
  weekDays: string[];
  hourLabels: string[];
  mapSelectedCityNormalized: string | null;
  mapSelectedRegionId: string | null;
  /** When this panel matches the active city cross-filter, localized titles include this city name. */
  filteredCityDisplayName: string | null;
  heatmapFilterLabel?: string | null;
  isFocusedView?: boolean;
}> = ({
  region,
  crossFilter,
  onCrossFilter,
  weekDays,
  hourLabels,
  mapSelectedCityNormalized,
  mapSelectedRegionId,
  filteredCityDisplayName,
  heatmapFilterLabel,
  isFocusedView = false,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [heatHover, setHeatHover] = useState<HeatmapHover | null>(null);
  const tooltipPreferSide = isRTL ? ("left" as const) : ("right" as const);
  const shapeKey = resolveRegionShapeKey(region);
  const regionSlug = inferPakistanRegionSlug(region.id, region.title);
  const isOthers = isOthersRegion(region);

  const panelTitles = useMemo(() => {
    if (!filteredCityDisplayName?.trim()) {
      return {
        regionHeading: region.title,
        cityMap: region.cityMapTitle,
        heatmap: region.heatmapTitle,
        donut: region.donutTitle,
      };
    }
    const city = filteredCityDisplayName.trim();
    return {
      regionHeading: city,
      cityMap: t("shopifyGeo.panel.cityMapTitleWithCity", {
        region: region.title,
        city,
      }),
      heatmap: t("shopifyGeo.panel.heatmapTitleWithCity", {
        region: region.title,
        city,
      }),
      donut: t("shopifyGeo.panel.donutTitleWithCity", {
        region: region.title,
        city,
      }),
    };
  }, [
    filteredCityDisplayName,
    region.title,
    region.cityMapTitle,
    region.heatmapTitle,
    region.donutTitle,
    t,
  ]);

  const regionSectionAria = filteredCityDisplayName?.trim()
    ? filteredCityDisplayName.trim()
    : t("shopifyGeo.regions.panelAria", {
        region: region.title,
        defaultValue: `${region.title} panel`,
      });

  const hasCityFilter = Boolean(filteredCityDisplayName?.trim());
  const hasHeatmapFilter = Boolean(heatmapFilterLabel?.trim());

  return (
    <section
      className={[
        "shopify-geo__region",
        isFocusedView ? "shopify-geo__region--focused" : "",
        hasCityFilter ? "shopify-geo__region--city-filter" : "",
        hasHeatmapFilter && !hasCityFilter ? "shopify-geo__region--heatmap-filter" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={regionSectionAria}
    >
      <div
        className={[
          "shopify-geo__region-top",
          hasCityFilter || hasHeatmapFilter ? "shopify-geo__region-top--filtered" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div
          className={`shopify-geo__region-icon shopify-geo__region-icon--${
            isOthers ? "others" : shapeKey
          }`}
          aria-hidden="true"
        />
        <div className="shopify-geo__region-heading">
          <h3>{panelTitles.regionHeading}</h3>
          {hasCityFilter ? (
            <span className="shopify-geo__region-badge">{region.title}</span>
          ) : null}
          {hasHeatmapFilter ? (
            <span className="shopify-geo__region-badge shopify-geo__region-badge--time">
              {heatmapFilterLabel}
            </span>
          ) : null}
        </div>
        {isOthers ? (
          <DefinitionTooltip
            label={panelTitles.regionHeading}
            info={{
              description: t("shopifyGeo.tooltips.othersRegion", {
                defaultValue:
                  "Customers with no region associated with them due to lack of available data fall in Others.",
              }),
            }}
            preferSide={tooltipPreferSide}
            formulaTitle={t("shopifyGeo.tooltips.formulaTitle")}
            exampleLabel={t("shopifyGeo.tooltips.exampleLabel")}
          />
        ) : null}
      </div>

      <div className="shopify-geo__metrics-grid">
        {region.metrics.map(metric => (
          <ShopifyGeoSparklineMetric
            key={metric.key}
            metric={metric}
            scopeLabel={panelTitles.regionHeading}
          />
        ))}
      </div>

      <div className="shopify-geo__panels-row">
      <div className="shopify-geo__panel">
        <h4>
          <span className="shopify-geo__panel-title-text">{panelTitles.cityMap}</span>
          <DefinitionTooltip
            label={panelTitles.cityMap}
            info={{
              description: t("shopifyGeo.tooltips.cityTotalSales", {
                region: region.title,
              }),
              formula: t("shopifyGeo.tooltips.cityTotalSalesFormula"),
            }}
            preferSide={tooltipPreferSide}
            formulaTitle={t("shopifyGeo.tooltips.formulaTitle")}
            exampleLabel={t("shopifyGeo.tooltips.exampleLabel")}
          />
        </h4>
        {isOthers ? (
          <ShopifyGeoNotAvailableMapPlaceholder label={region.title} />
        ) : (
          <ShopifyGeoPakistanCitiesMap
            region={region}
            shapeKey={shapeKey}
            onCityPinClick={(city, filterRegion) =>
              onCrossFilter(applyCityCrossFilter(crossFilter, filterRegion, city))
            }
            selectedCityNormalized={mapSelectedCityNormalized}
            selectedRegionId={mapSelectedRegionId}
            crossFilterCityLabel={filteredCityDisplayName}
          />
        )}
      </div>

      <div className="shopify-geo__panel">
        <h4>
          <span className="shopify-geo__panel-title-text">{panelTitles.heatmap}</span>
          <DefinitionTooltip
            label={panelTitles.heatmap}
            info={{
              description: t("shopifyGeo.tooltips.heatmapCrossFilter"),
              formula: t("shopifyGeo.tooltips.heatmapFormula"),
            }}
            preferSide={tooltipPreferSide}
            formulaTitle={t("shopifyGeo.tooltips.formulaTitle")}
            exampleLabel={t("shopifyGeo.tooltips.exampleLabel")}
          />
        </h4>
        <div className="shopify-geo__heatmap-wrap">
          <div className="shopify-geo__heat-days">
            <span />
            {weekDays.map(day => (
              <span key={day}>{day}</span>
            ))}
          </div>
          <div className="shopify-geo__heat-body">
            {region.heatmap.map((row, rowIndex) => (
              <div className="shopify-geo__heat-row" key={`${regionSlug}-row-${rowIndex}`}>
                <span>{hourLabels[rowIndex]}</span>
                {row.map((value, colIndex) => {
                  const rawOrders = region.heatmapRaw?.[rowIndex]?.[colIndex] ?? value;
                  const heatActive =
                    crossFilter?.filter_region === regionSlug &&
                    crossFilter.filter_hour === rowIndex &&
                    crossFilter.filter_weekday === colIndex;
                  return (
                    <div
                      key={`${regionSlug}-heat-${rowIndex}-${colIndex}`}
                      role="button"
                      tabIndex={0}
                      className={`shopify-geo__heat-cell ${heatLevelClass(value)}${heatActive ? " shopify-geo__heat-cell--active" : ""}`}
                      onMouseMove={event => {
                        setHeatHover({
                          clientX: event.clientX,
                          clientY: event.clientY,
                          hour: hourLabels[rowIndex],
                          day: weekDays[colIndex],
                          orders: rawOrders,
                        });
                      }}
                      onMouseLeave={() => setHeatHover(null)}
                      onClick={() =>
                        onCrossFilter(
                          applyHeatmapCrossFilter(
                            crossFilter,
                            regionSlug,
                            rowIndex,
                            colIndex,
                          ),
                        )
                      }
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onCrossFilter(
                            applyHeatmapCrossFilter(
                              crossFilter,
                              regionSlug,
                              rowIndex,
                              colIndex,
                            ),
                          );
                        }
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
          {heatHover && createPortal(
            <div
              className="analytics-chart-tooltip analytics-chart-tooltip--shopify-heatmap"
              style={getFixedTooltipPosition(
                heatHover.clientX,
                heatHover.clientY,
                220,
                66,
                12,
                8,
                isRTL,
              )}
            >
              <div className="analytics-chart-tooltip__bar" />
              <div className="analytics-chart-tooltip__content">
                <span className="analytics-chart-tooltip__meta">
                  {heatHover.day} {heatHover.hour}
                </span>
                <span className="analytics-chart-tooltip__meta">
                  <strong>
                    {geoDigits(
                      t("shopifyGeo.tooltips.ordersCount", { count: heatHover.orders }),
                      isRTL,
                    )}
                  </strong>
                </span>
              </div>
            </div>,
            document.body,
          )}
        </div>
      </div>

      <div className="shopify-geo__panel">
        <h4>
          <span className="shopify-geo__panel-title-text">{panelTitles.donut}</span>
          <DefinitionTooltip
            label={panelTitles.donut}
            info={{
              description: t("shopifyGeo.tooltips.topProductsTotalSales", {
                region: region.title,
              }),
              formula: t("shopifyGeo.tooltips.topProductsFormula", { region: region.title }),
            }}
            preferSide={tooltipPreferSide}
            formulaTitle={t("shopifyGeo.tooltips.formulaTitle")}
            exampleLabel={t("shopifyGeo.tooltips.exampleLabel")}
          />
        </h4>
        <div className="shopify-geo__product-bars-block">
          <ShopifyGeoProductSalesBarChart products={region.products} />
        </div>
      </div>
      </div>
    </section>
  );
};

export default ShopifyGeoRegionCard;
